// Rozszerzenie FiltersSidebar o obsługę typu 'range' (cena, dostępność) i rozwijane sekcje z checkboxami
// @ts-nocheck

import React, { FC, useEffect, useState, useMemo } from 'react';
import { ExclamationCircle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import classnames from 'classnames';

import { IProductsFilter, IProductsFilterValue } from 'api/types';
import { Range, Collapse, Checkbox } from 'components/controls';
import { XIcon } from 'assets/icons';
import RangeInline from 'components/controls/Range/RangeInline';

import styles from 'theme/pages/Products/components/FiltersSidebar/FiltersSidebar.module.scss';

/** Wszystkie filtry z type === "bool" grupowane pod jednym nagłówkiem "Flagi". */
const FLAGS_SECTION_ID = 'flags';

interface IFilter {
  filter_id: string;
  filter_value: string;
}

// typ danych wejściowych
interface IProps {
  categoryId?: number;
  queryFilters: IFilter[];
  filtersData?: IProductsFilter[];
  onChange: (filters: IFilter[]) => void;
  productsCount?: number; // Liczba produktów do wyświetlenia na przycisku
  onFiltersChange?: (filters: IFilter[]) => void; // Callback dla mobile overlay - zwraca aktualne filtry
  hideActions?: boolean; // Ukryj przyciski akcji (dla mobile overlay)
}

const FiltersSidebar: FC<IProps> = ({
  categoryId,
  onChange,
  queryFilters,
  filtersData,
  productsCount,
  onFiltersChange,
  hideActions = false
}) => {
  const { t } = useTranslation();

  // lista zqaznaczonych filtrów
  const [currentProductsFilters, setCurrentProductsFilters] = useState<
    { filter_id: string; filter_value: string }[]
  >([]);

  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(new Set([FLAGS_SECTION_ID]));

  // nadpisanie lokalnych filtrów przy zmienie filtrów z props'ów
  useEffect(() => {
    setCurrentProductsFilters(queryFilters);
  }, [queryFilters]);

  // Wywołaj callback gdy zmienią się filtry (dla mobile overlay)
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(currentProductsFilters);
    }
  }, [currentProductsFilters, onFiltersChange]);

  // Funkcja do przełączania sekcji filtra
  const toggleFilter = (filterId: string) => {
    setExpandedFilters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(filterId)) {
        newSet.delete(filterId);
      } else {
        newSet.add(filterId);
      }
      return newSet;
    });
  };

  // Funkcja do obsługi checkboxa
  const handleCheckboxChange = (filterId: string, filterValue: string, checked: boolean) => {
    setCurrentProductsFilters((prevState) => {
      if (checked) {
        // Dodajemy filtr, jeśli nie istnieje
        const exists = prevState.some(
          (f) => f.filter_id === filterId && f.filter_value === filterValue
        );
        if (!exists) {
          return [...prevState, { filter_id: filterId, filter_value: filterValue }];
        }
      } else {
        // Usuwamy filtr
        return prevState.filter(
          (f) => !(f.filter_id === filterId && f.filter_value === filterValue)
        );
      }
      return prevState;
    });
  };

  // Sprawdzenie, czy checkbox jest zaznaczony
  const isChecked = (filterId: string, filterValue: string) => {
    return currentProductsFilters.some(
      (f) => f.filter_id === filterId && f.filter_value === filterValue
    );
  };

  const { flagFilters, otherFiltersSorted } = useMemo(() => {
    if (!filtersData) return { flagFilters: [], otherFiltersSorted: [] };
    const flag: IProductsFilter[] = [];
    const other: IProductsFilter[] = [];
    filtersData.forEach((f) => {
      if (f.type === 'bool') {
        flag.push(f);
      } else {
        other.push(f);
      }
    });
    const sorted = [...other].sort((a, b) => {
      if (a.type === 'range' && b.type !== 'range') return 1;
      if (a.type !== 'range' && b.type === 'range') return -1;
      return 0;
    });
    return { flagFilters: flag, otherFiltersSorted: sorted };
  }, [filtersData]);

  // Nie udostępniamy filtrowania po cenie – ukrywamy w UI (display:none)
  const isPriceFilter = (filter: IProductsFilter) => {
    const id = (filter?.id || '').toLowerCase();
    const label = (filter?.label || '').toLowerCase();
    return id.includes('price') || id.includes('cena') || label === 'cena' || label.includes('price');
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Products-components-FiltersSidebar'
      )}>
      {!categoryId && (
        <div className={styles.info}>
          <ExclamationCircle />
          <span>
            <ReactMarkdown disallowedElements={['p']} unwrapDisallowed>
              {t('PRODUCTS_SIDEBAR_NO_FILTERS_INFO')}
            </ReactMarkdown>
          </span>
        </div>
      )}

      {!!categoryId && (filtersData?.length || 0) > 0 && (
        <>
          <div className={styles.filtersWrapper}>
            {flagFilters.length > 0 && (
              <div key={FLAGS_SECTION_ID}>
                <Collapse
                  title={t('Flagi')}
                  open={expandedFilters.has(FLAGS_SECTION_ID)}
                  onClick={() => toggleFilter(FLAGS_SECTION_ID)}>
                  <div className={styles.filterContent}>
                    <div className={styles.checkboxList}>
                      {flagFilters.map((filter) =>
                        filter.values.map((value) => (
                              <div
                                key={`${filter.id}-${value.value}`}
                                className={styles.checkboxItem}>
                                <Checkbox
                                  checked={isChecked(filter.id, value.value)}
                                  onClick={() =>
                                    handleCheckboxChange(
                                      filter.id,
                                      value.value,
                                      !isChecked(filter.id, value.value)
                                    )
                                  }
                                />
                                <label
                                  onClick={() =>
                                    handleCheckboxChange(
                                      filter.id,
                                      value.value,
                                      !isChecked(filter.id, value.value)
                                    )
                                  }>
                                  {value.name}
                                </label>
                              </div>
                            ))
                      )}
                    </div>
                  </div>
                </Collapse>
              </div>
            )}
            {otherFiltersSorted.map((filter) => (
              <div
                key={filter.id}
                className={classnames({ [styles.hiddenPriceFilter]: isPriceFilter(filter) })}>
                <Collapse
                  title={filter.label}
                  open={expandedFilters.has(filter.id)}
                  onClick={() => toggleFilter(filter.id)}>
                  <div className={styles.filterContent}>
                    {(filter.type === 'singlechoice' || filter.type === 'bool') ? (
                      <div className={styles.checkboxList}>
                        {filter.values.map((value) => (
                          <div key={value.value} className={styles.checkboxItem}>
                            <Checkbox
                              checked={isChecked(filter.id, value.value)}
                              onClick={() =>
                                handleCheckboxChange(
                                  filter.id,
                                  value.value,
                                  !isChecked(filter.id, value.value)
                                )
                              }
                            />
                            <label
                              onClick={() =>
                                handleCheckboxChange(
                                  filter.id,
                                  value.value,
                                  !isChecked(filter.id, value.value)
                                )
                              }>
                              {value.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    ) : filter.type === 'range' ? (
                      <RangeInline
                        onChange={(newFilters) => setCurrentProductsFilters(newFilters)}
                        queryFilters={currentProductsFilters}
                        filter={{ ...filter, values: filter.values }}
                      />
                    ) : (
                      <div className={styles.unknownType}>
                        {t('Nieznany typ filtra')} (<strong>{filter.type}</strong>)
                      </div>
                    )}
                  </div>
                </Collapse>
              </div>
            ))}
          </div>

          {!hideActions && (
            <div className={styles.actions}>
              <button
                className={styles.filterButton}
                onClick={() => onChange(currentProductsFilters)}>
                {t('FILTRUJ')}
                {productsCount !== undefined && ` (${productsCount})`}
              </button>
              <button
                className={styles.clearButton}
                onClick={() => {
                  setCurrentProductsFilters([]);
                  onChange([]);
                }}>
                {t('Wyczyść wszystko')} <XIcon />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FiltersSidebar;
